import { Syringe, Droplet } from 'lucide-react';
import { Medication } from './index';

export const injectablesMedications: Medication[] = [
  {
    id: '27',
    name: 'Glutathione',
    description: 'A powerful antioxidant that supports detoxification, immune function, and skin health.',
    research: 'Glutathione in Health - Glutathione, a key antioxidant, detoxifies free radicals and supports liver function. IV administration enhances immune response and skin brightness, with studies showing reduced oxidative stress.',
    url: 'https://www.jstage.jst.go.jp/article/jcbn/40/2/40_2_115/_article',
    category: 'injectables',
    Icon: Syringe,
    video: 'https://www.youtube.com/watch?v=2h2KxYfGJzY',
    availablePreparations: 'Injection (e.g., 200 mg/ml)',
    clinicalDetails: {
      dosingGuidelines: '600-1200 mg IV weekly or 200-400 mg IM daily (compounded).',
      administrationProtocols: 'IV infusion over 15-30 minutes or IM injection; dilute IV appropriately.',
      contraindications: 'Hypersensitivity; caution in asthma (IV may trigger bronchospasm).',
      monitoringRequirements: 'Monitor skin changes, oxidative stress markers; assess injection reactions.'
    },
    adverseReactions: {
      common: 'Injection site reactions, headache.',
      lessCommon: 'Rash, bronchospasm (IV).',
      rare: 'Allergic reactions, hypotension.'
    }
  },
  {
    id: '28',
    name: 'NAD+ (Nicotinamide Adenine Dinucleotide)',
    description: 'A coenzyme essential for energy production and cellular repair. Used for anti-aging, brain function, and metabolic health.',
    research: 'NAD+ in Aging - NAD+, a coenzyme, boosts cellular energy and repair via sirtuin activation. IV NAD+ therapy improves cognition and metabolism in aging patients, per early clinical data.',
    url: 'https://www.science.org/doi/10.1126/science.aam6640',
    category: 'injectables',
    Icon: Syringe,
    video: 'https://www.youtube.com/watch?v=2h2KxYfGJzY',
    availablePreparations: 'Injection (e.g., 200 mg/ml)',
    clinicalDetails: {
      dosingGuidelines: '100-1000 mg IV over 1-4 hours, weekly or monthly (compounded).',
      administrationProtocols: 'Slow IV infusion to minimize flushing; dilute in saline.',
      contraindications: 'Hypersensitivity; caution in hypotension (limited data).',
      monitoringRequirements: 'Monitor energy, cognition; assess flushing, BP changes during infusion.'
    },
    adverseReactions: {
      common: 'Flushing, nausea, headache during infusion.',
      lessCommon: 'Dizziness, chest tightness.',
      rare: 'Allergic reactions, hypotension.'
    }
  },
  {
    id: '29',
    name: 'L-Carnitine',
    description: 'An amino acid that enhances fat metabolism, energy production, and exercise performance.',
    research: 'L-Carnitine and Metabolism - L-Carnitine transports fatty acids into mitochondria, enhancing fat burning and energy. Injectable forms improve exercise performance by 5-10% in trials, aiding weight loss.',
    url: 'https://www.mdpi.com/2072-6643/8/10/620',
    category: 'injectables',
    Icon: Syringe,
    video: 'https://www.youtube.com/watch?v=2h2KxYfGJzY',
    availablePreparations: 'Injection (e.g., 500 mg/ml; in SLIM BOOST and LIPO-C)',
    clinicalDetails: {
      dosingGuidelines: '500-2000 mg IV/IM daily or weekly (compounded).',
      administrationProtocols: 'IV bolus/infusion or IM injection; rotate sites.',
      contraindications: 'Hypersensitivity, severe renal impairment (without dialysis).',
      monitoringRequirements: 'Monitor energy, weight; assess renal function, GI side effects.'
    },
    adverseReactions: {
      common: 'Nausea, vomiting, diarrhea.',
      lessCommon: 'Fishy odor, injection site reactions.',
      rare: 'Seizures (high doses), allergic reactions.'
    }
  },
  {
    id: '30',
    name: 'MIC (Methionine, Inositol, Choline)',
    description: 'A lipotropic blend that aids in fat metabolism and liver function, often used for weight management.',
    research: 'Lipotropic Agents - MIC injections promote fat metabolism and liver health. Studies show modest reductions in visceral fat (5-7%) when combined with diet, supporting weight management.',
    url: 'https://www.hindawi.com/journals/jobe/2011/297315/',
    category: 'injectables',
    Icon: Syringe,
    availablePreparations: 'Injection (e.g., 25/50/50 mg/ml with Methylcobalamin; in LIPO-C, Super MIC, SLIM BOOST)',
    clinicalDetails: {
      dosingGuidelines: '25/50/50 mg/mL; 1-2 mL IM weekly (compounded).',
      administrationProtocols: 'IM injection in gluteal or deltoid; rotate sites.',
      contraindications: 'Hypersensitivity, severe liver/renal disease.',
      monitoringRequirements: 'Monitor weight, liver function; assess injection site reactions.'
    },
    adverseReactions: {
      common: 'Injection site pain, nausea.',
      lessCommon: 'Diarrhea, headache.',
      rare: 'Allergic reactions, hypotension.'
    }
  },
  {
    id: '32',
    name: 'Super MIC',
    description: 'An enhanced MIC variant with vitamins and amino acids for weight loss and metabolism support.',
    research: 'Enhanced Lipotropics - Super MIC amplifies fat loss and energy by 10% over standard MIC formulations in preliminary research.',
    url: 'https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5579616/',
    category: 'injectables',
    Icon: Syringe,
    availablePreparations: 'Injection (e.g., with B12, L-Carnitine)',
    clinicalDetails: {
      dosingGuidelines: '1-2 mL IM weekly (e.g., MIC + B12/L-Carnitine; compounded).',
      administrationProtocols: 'IM injection in gluteal or deltoid; rotate sites.',
      contraindications: 'Hypersensitivity, severe liver/renal disease.',
      monitoringRequirements: 'Monitor weight, energy, liver function; check injection reactions.'
    },
    adverseReactions: {
      common: 'Injection site reactions, nausea, diarrhea.',
      lessCommon: 'Headache, dizziness.',
      rare: 'Allergic reactions, vitamin toxicity.'
    }
  },
  {
    id: '33',
    name: 'Ascorbic Acid (Vitamin C)',
    description: 'High-dose injectable Vitamin C supporting immunity and skin health.',
    research: 'IV Vitamin C Benefits - High-dose vitamin C reduces inflammation and cold duration by 8%, while supporting collagen synthesis and immune function.',
    url: 'https://journals.lww.com/ccmjournal/Abstract/2017/03000/Phase_I_Safety_Trial_of_Intravenous_Ascorbic_Acid.9.aspx',
    category: 'injectables',
    Icon: Syringe,
    video: 'https://www.youtube.com/watch?v=2h2KxYfGJzY',
    availablePreparations: 'Injection (e.g., 450 mg/ml)',
    clinicalDetails: {
      dosingGuidelines: '10-50 g IV weekly (compounded; high-dose protocols).',
      administrationProtocols: 'IV infusion over 30-60 minutes; dilute in saline.',
      contraindications: 'G6PD deficiency, renal stones, hypersensitivity.',
      monitoringRequirements: 'Monitor renal function, urine pH; assess GI upset, infusion reactions.'
    },
    adverseReactions: {
      common: 'Injection site pain, nausea, diarrhea.',
      lessCommon: 'Kidney stones (high doses), hemolysis (G6PD deficiency).',
      rare: 'Allergic reactions, hyperoxaluria.'
    }
  }
];
